import { useState, useCallback } from 'react'

export type ImageRatio = 'portrait' | 'landscape' | 'square' | 'loading'

interface UseImageRatioReturn {
  imageRatio: ImageRatio
  isError: boolean
  handleImageLoad: (e: React.SyntheticEvent<HTMLImageElement>) => void
  handleImageError: () => void
  getImageClasses: () => string
  getBackgroundStyle: (imageUrl: string) => React.CSSProperties
}

export const useImageRatio = (debugUrl?: string): UseImageRatioReturn => {
  const [imageRatio, setImageRatio] = useState<ImageRatio>('loading')
  const [isError, setIsError] = useState(false)

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget
      const ratio = img.naturalWidth / img.naturalHeight

      if (ratio > 1.2) {
        setImageRatio('landscape') // 가로형
      } else if (ratio < 0.8) {
        setImageRatio('portrait') // 세로형
      } else {
        setImageRatio('square') // 정사각형에 가까움
      }
    },
    []
  )

  const handleImageError = useCallback(() => {
    if (process.env.NODE_ENV === 'development' && debugUrl) {
      console.error('이미지 로드 실패:', debugUrl)
    }
    setIsError(true)
  }, [debugUrl])

  const getImageClasses = useCallback(() => {
    switch (imageRatio) {
      case 'portrait':
        return 'w-full h-full object-contain' // 세로 이미지: 전체 보이기
      case 'landscape':
        return 'w-full h-full object-contain' // 가로 이미지: 전체 보이기
      case 'square':
        return 'w-full h-full object-cover' // 정사각형: 꽉 채우기
      default:
        return 'w-full h-full object-cover' // 로딩 중이나 에러시 기본값
    }
  }, [imageRatio])

  const getBackgroundStyle = useCallback(
    (imageUrl: string): React.CSSProperties => {
      if (imageRatio === 'loading' || imageRatio === 'square') {
        return {}
      }

      // 세로/가로 이미지의 경우 블러된 배경 추가
      return {
        backgroundColor: 'black',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }
    },
    [imageRatio]
  )

  return {
    imageRatio,
    isError,
    handleImageLoad,
    handleImageError,
    getImageClasses,
    getBackgroundStyle,
  }
}
